"use client";

import { uploadFile, uploadManagedFile as uploadManagedBlobFile } from "@/utils/upload-file";
import { toast } from "sonner";
import { prepareServerPhoto, SERVER_PHOTO_MAX_BYTES } from "./server-photo";

const HEIC_FILE_EXTENSION_PATTERN = /\.(?:heic|heif)$/iu;
const HEIC_MIME_TYPES = new Set([
  "image/heic",
  "image/heic-sequence",
  "image/heif",
  "image/heif-sequence",
]);
const JPEG_MIME_TYPE = "image/jpeg";

type HeicPhotoConverter = (file: File) => Promise<Blob>;

export function isHeicPhoto(file: Pick<File, "name" | "type">) {
  return HEIC_MIME_TYPES.has(file.type.toLowerCase())
    || HEIC_FILE_EXTENSION_PATTERN.test(file.name);
}

function jpegFileName(fileName: string) {
  if (HEIC_FILE_EXTENSION_PATTERN.test(fileName)) {
    return fileName.replace(HEIC_FILE_EXTENSION_PATTERN, ".jpg");
  }

  return `${fileName}.jpg`;
}

async function convertHeicPhoto(file: File) {
  const { convertHeicPreview } = await import("@/components/heic-preview.client");
  return convertHeicPreview(file);
}

export async function preparePhotoUploadFile(
  file: File,
  converter: HeicPhotoConverter = convertHeicPhoto,
) {
  if (!isHeicPhoto(file)) {
    return file;
  }

  try {
    const converted = await converter(file);
    if (converted.size === 0) {
      throw new Error("Converted photo is empty");
    }

    return new File([converted], jpegFileName(file.name), {
      lastModified: file.lastModified,
      type: JPEG_MIME_TYPE,
    });
  } catch (error) {
    throw new Error(
      "We couldn't prepare this HEIC photo. Please try a JPEG or PNG instead.",
      { cause: error },
    );
  }
}

export function resetFileInput(
  ref: React.RefObject<HTMLInputElement | null>,
) {
  if (ref.current) {
    ref.current.value = "";
  }
}

type MaxFileSizeValidationOptions = {
  actionHint?: string;
  fileLabel?: string;
};

export function formatFileSizeLimit(sizeInMB: number) {
  if (sizeInMB >= 1024 && sizeInMB % 1024 === 0) {
    return `${sizeInMB / 1024}GB`;
  }

  return `${sizeInMB}MB`;
}

function getMaxFileSizeErrorMessage(
  maxSizeInMB: number,
  options?: MaxFileSizeValidationOptions,
) {
  const fileLabel = options?.fileLabel ?? "File";
  const message = `${fileLabel} must be ${formatFileSizeLimit(maxSizeInMB)} or smaller.`;

  return options?.actionHint ? `${message} ${options.actionHint}` : message;
}

export function validateMaxFileSize(
  file: File,
  maxSizeInMB?: number,
  options?: MaxFileSizeValidationOptions,
) {
  if (!maxSizeInMB) {
    return true;
  }

  const fileSizeInMB = file.size / (1024 * 1024);
  if (fileSizeInMB > maxSizeInMB) {
    toast.error(getMaxFileSizeErrorMessage(maxSizeInMB, options));
    return false;
  }

  return true;
}

function isAcceptedFileType(
  file: File,
  acceptedFileTypes?: string[],
) {
  if (!acceptedFileTypes?.length || acceptedFileTypes.includes("*")) {
    return true;
  }

  return acceptedFileTypes.some((acceptedFileType) => {
    if (acceptedFileType === "image/*" && isHeicPhoto(file)) {
      return true;
    }

    if (acceptedFileType === file.type) {
      return true;
    }

    if (!acceptedFileType.endsWith("/*")) {
      return false;
    }

    const prefix = acceptedFileType.slice(0, -1);
    return file.type.startsWith(prefix);
  });
}

export function validateAcceptedFileTypes(
  file: File,
  acceptedFileTypes?: string[],
) {
  if (!isAcceptedFileType(file, acceptedFileTypes)) {
    toast.error(`File must be one of: ${acceptedFileTypes?.join(", ")}`);
    return false;
  }

  return true;
}

type ManagedFileUploadInput = {
  applicationId?: string | null;
  file: File;
  organizationMembershipId?: string | null;
  userId?: string | null;
  maxSizeInMB?: number;
  acceptedFileTypes?: string[];
};

export async function uploadManagedFile({
  applicationId,
  file,
  organizationMembershipId,
  userId,
  maxSizeInMB,
  acceptedFileTypes,
}: ManagedFileUploadInput) {
  return uploadManagedBlobFile({
    applicationId: applicationId ?? undefined,
    file,
    organizationMembershipId: organizationMembershipId ?? undefined,
    userId: userId ?? undefined,
    maximumSizeInBytes: maxSizeInMB ? maxSizeInMB * 1024 * 1024 : undefined,
    allowedContentTypes: acceptedFileTypes,
  });
}

export async function uploadManagedPhotoFile(input: ManagedFileUploadInput) {
  const { file, maxSizeInMB } = input;
  if (maxSizeInMB && file.size > maxSizeInMB * 1024 * 1024) {
    throw new Error(getMaxFileSizeErrorMessage(maxSizeInMB));
  }

  const preparedFile = await preparePhotoUploadFile(file);
  if (maxSizeInMB && preparedFile.size > maxSizeInMB * 1024 * 1024) {
    throw new Error(getMaxFileSizeErrorMessage(maxSizeInMB));
  }

  if (!isAcceptedFileType(preparedFile, input.acceptedFileTypes)) {
    throw new Error("File type is not allowed");
  }

  const serverPhoto = await prepareServerPhoto(preparedFile);
  if (!isAcceptedFileType(serverPhoto, input.acceptedFileTypes)) {
    throw new Error("This photo needs to be resized. Please choose a smaller file in an allowed format.");
  }
  let uploadPhoto = serverPhoto;
  if (serverPhoto.size <= SERVER_PHOTO_MAX_BYTES) {
    try {
      const bytes = await serverPhoto.arrayBuffer();
      uploadPhoto = new File([bytes], serverPhoto.name, {
        type: serverPhoto.type,
        lastModified: serverPhoto.lastModified,
      });
    } catch (error) {
      throw new Error("We couldn't read this photo from your device. Save or export it to a local folder, then select that copy and try again.", { cause: error });
    }
  }
  try {
    if (serverPhoto.size > SERVER_PHOTO_MAX_BYTES) {
      // Preserve formats such as GIF and SVG instead of flattening their content.
      return await uploadManagedFile({ ...input, file: serverPhoto });
    }

    return await uploadFile({
      applicationId: input.applicationId ?? undefined,
      organizationMembershipId: input.organizationMembershipId ?? undefined,
      userId: input.userId ?? undefined,
      file: uploadPhoto,
      uploadMode: "server",
    });
  } catch (error) {
    if (
      error instanceof TypeError
      && /^(?:Failed to fetch|Load failed|NetworkError when attempting to fetch resource\.|Network request failed)(?: \([^)]+\))?$/.test(error.message)
    ) {
      throw new Error("We couldn't upload your photo. Check your connection and try again.", { cause: error });
    }
    throw error;
  }
}
